import { CloseButton, ContentPlaceholder, ContentWrapper } from 'components';
import { isEmpty } from 'lodash/fp';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { doMarkNotificationsAsRead } from 'store/actionCreators/profile';
import {
  getAuthUser,
  getNotifications,
  getNotificationsUnreadCount,
} from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import { IN_APP_NOTIFICATION_TYPE, UserNotificationItem } from 'types';
import { getNotificationDate } from 'utils/dates';

import {
  Message,
  MessageWrapper,
  NotificationDate,
  NotificationImage,
  NotificationPlaceholder,
  NotificationTitle,
  NotificationWrapper,
  StatusIndicator,
  StyledArrowIcon,
  StyledContent,
  StyledHeader,
} from './styled';

const getInitials = (name?: string | null) => {
  if (!name?.trim()) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getTypeInitials = (type: UserNotificationItem['type']) => {
  switch (type) {
    case IN_APP_NOTIFICATION_TYPE.EVENT:
      return 'EV';
    case IN_APP_NOTIFICATION_TYPE.COMMUNITY:
      return 'CO';
    case IN_APP_NOTIFICATION_TYPE.SUBSCRIPTION:
      return 'G';
    case IN_APP_NOTIFICATION_TYPE.MESSAGE:
      return 'M';
    default:
      return 'G';
  }
};

const NotificationAvatar: FC<{
  image: string | null;
  communityLogo?: string | null;
  initials: string;
}> = ({ image, communityLogo, initials }) => {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : image || communityLogo;

  if (src) {
    return (
      <NotificationImage src={src} alt="" onError={() => setFailed(true)} />
    );
  }

  return (
    <NotificationPlaceholder aria-hidden>{initials}</NotificationPlaceholder>
  );
};

const NotificationsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUser);
  const notifications = useSelector(getNotifications) || [];
  const notificationsUnreadCount = useSelector(getNotificationsUnreadCount);
  const communities = useSelector(getCommunities);

  useEffect(() => {
    if (notificationsUnreadCount) {
      dispatch(
        doMarkNotificationsAsRead({
          userId: user.userId,
          notifications: [],
        }),
      );
    }
  }, [notifications]);

  const goToProfile = () => {
    navigate('/profile');
  };

  const getCommunity = useCallback(
    (notification: UserNotificationItem) =>
      communities.find((c) => c.id === notification.communityId),
    [communities],
  );

  const formatNotificationLink = (link: string) => {
    if (link) {
      return link.replace('communities', 'community');
    }

    return null;
  };

  return (
    <ContentWrapper>
      <StyledHeader>
        {t('common.notifications')}
        <CloseButton hidden={false} onClick={goToProfile} />
      </StyledHeader>
      <StyledContent>
        {isEmpty(notifications) ? (
          <ContentPlaceholder
            placeholder={t('profileSettings.notificationsPlaceholder')}
          />
        ) : (
          notifications.map((notification) => {
            const community = getCommunity(notification);

            return (
              <NotificationWrapper
                key={notification.id}
                href={formatNotificationLink(notification.link)}
                isRead={notification.isRead}
              >
                <MessageWrapper>
                  <StatusIndicator isRead={notification.isRead} />
                  <NotificationAvatar
                    image={notification.image}
                    communityLogo={community?.logo}
                    initials={
                      getInitials(community?.name) ||
                      getTypeInitials(notification.type)
                    }
                  />
                  <Message>
                    {notification.createdAt && (
                      <NotificationDate>
                        {`${
                          community ? `${community.name} • ` : ''
                        }${getNotificationDate(t, notification.createdAt)}`}
                      </NotificationDate>
                    )}
                    {notification.title ? (
                      <NotificationTitle>
                        {notification.title}
                      </NotificationTitle>
                    ) : null}
                    {notification.message ? (
                      <div>{notification.message}</div>
                    ) : null}
                  </Message>
                </MessageWrapper>
                {notification.link && <StyledArrowIcon />}
              </NotificationWrapper>
            );
          })
        )}
      </StyledContent>
    </ContentWrapper>
  );
};

export default NotificationsPage;
